/**
 * Achievements: the facts they are measured by, asked of the database; which
 * were earned, remembered in it; and a toast the moment a new one is. What each
 * one *is* lives in `lib/achievements.js`, in code.
 *
 * `startedOn` (a setting) is the day you started using OKANE, and nothing
 * before it counts. It is written the first time a ledger is opened without
 * one: the day the oldest thing in it was *entered* (`created_at`, not the day
 * a transaction happened — an imported statement reaches back months), or
 * today for an empty ledger.
 */
import { computed, watch } from 'vue'
import { state, toast } from './state'
import { count } from './usage'
import { fromDb } from './live'
import { setSetting } from './settings'
import { transactionsBetween } from './transactions'
import { t } from './i18n'
import { query, run } from '../db'
import { measureAchievements } from '../lib/achievements'
import { buildSchedule } from '../lib/matching'
import { goalPlan } from '../lib/savings'
import { endOfMonth, monthOf, shiftMonth, startOfMonth, toISO, today as todayISO } from '../lib/dates'

/** A stored timestamp as the local day it fell on. */
const dayOf = (stamp) => {
  const date = new Date(stamp)
  return Number.isNaN(date.getTime()) ? null : toISO(date)
}

const ENTERED = ['transactions', 'goals', 'plans', 'budgets', 'categories', 'tags']

/** Makes sure the ledger knows when you started: see above. */
export async function ensureStartedOn() {
  if (state.settings.startedOn) return
  const [row] = await query(
    `SELECT min(first) AS first FROM (${ENTERED.map((table) => `SELECT min(created_at) AS first FROM ${table}`).join(' UNION ALL ')})`,
  )
  const day = (row?.first && dayOf(row.first)) || todayISO()
  // A viewer of a shared ledger cannot write it; the day still holds for them.
  await setSetting('startedOn', day).catch(() => (state.settings.startedOn = day))
}

/** What has been earned, from the database. */
export async function loadAchievements() {
  state.achievements = await query('SELECT id, earned_on FROM achievements ORDER BY earned_on, id')
}

/**
 * What the achievements are measured by, asked of the database: money in and
 * out per month, the days anything was spent, and the plan occurrences that
 * came as planned. Re-asked after every write, like every other figure.
 */
const facts = fromDb(
  () => ({
    ready: state.ready,
    startedOn: state.settings.startedOn,
    today: todayISO(),
    // What the plans are is part of the question, not only the ledger.
    plans: state.plans.map((p) => `${p.id}:${p.updated_at}:${p.archived}`).join(),
  }),
  async ({ ready, startedOn, today }) => {
    if (!ready || !startedOn) return null
    const first = shiftMonth(monthOf(startedOn), -1)
    const last = shiftMonth(monthOf(today), -1)

    const sums = await query(
      `SELECT substr(date, 1, 7) AS month,
              total(CASE WHEN amount > 0 THEN amount END) AS income,
              total(CASE WHEN amount < 0 THEN -amount END) AS spent
         FROM transactions WHERE date BETWEEN ? AND ? GROUP BY 1`,
      [startOfMonth(first), endOfMonth(last)],
    )
    const byMonth = new Map(sums.map((r) => [r.month, r]))
    const months = []
    for (let m = first; m <= last; m = shiftMonth(m, 1)) {
      months.push({ month: m, income: byMonth.get(m)?.income ?? 0, spent: byMonth.get(m)?.spent ?? 0 })
    }

    const spendDays = (
      await query('SELECT DISTINCT date FROM transactions WHERE amount < 0 AND date BETWEEN ? AND ? ORDER BY date', [
        startedOn,
        today,
      ])
    ).map((r) => r.date)

    // Every occurrence due since the start, against every row in the plans'
    // categories up to today: an automatic plan's figure is an average of the
    // times before, which may reach back past the start.
    const plans = state.plans.filter((p) => !p.archived && p.category_id)
    let planMatches = []
    if (plans.length) {
      const rows = await transactionsBetween('0000-01-01', today, {
        categoryIds: [...new Set(plans.map((p) => p.category_id))],
      })
      planMatches = buildSchedule(plans, rows, { from: startedOn, to: today, today })
        .filter((row) => row.status === 'matched')
        // It came as planned on the day the last of its money did.
        .map((row) => row.matches.reduce((day, tx) => (tx.date > day ? tx.date : day), row.due))
        .filter((day) => day <= today)
        .sort()
    }
    return { today, startedOn, months, spendDays, planMatches }
  },
  null,
)

/** A goal is done the day its last instalment is due — or the day it began, if it began full. */
const goalFacts = computed(() => {
  const today = todayISO()
  return state.goals.map((goal) => {
    const plan = goalPlan(goal, today)
    const done = plan.target > 0 ? (plan.initial >= plan.target ? plan.start : plan.end) : null
    return { createdOn: dayOf(goal.created_at), completedOn: plan.complete && done && done <= today ? done : null }
  })
})

/** Whether the facts are in: until then nothing is earned or unearned. */
export const achievementsReady = computed(() => !!facts.value)

/** Every achievement, measured, the recorded ones kept (`measureAchievements`). */
export const achievements = computed(() => {
  const f = facts.value
  const recorded = new Map((state.achievements ?? []).map((r) => [r.id, r.earned_on]))
  return measureAchievements(f && { ...f, goals: goalFacts.value }, recorded)
})

export const earnedCount = computed(() => achievements.value.filter((a) => a.earned).length)

/** Ids already written or announced this session, so none is announced twice. */
const announced = new Set()

/**
 * Records what has newly been earned, and says so. A viewer cannot write the
 * ledger: they are told once, and it is left at that.
 */
export async function recordAchievements() {
  if (!achievementsReady.value) return []
  const fresh = achievements.value.filter((a) => a.earned && !a.recorded && !announced.has(a.id))
  if (!fresh.length) return []
  for (const a of fresh) {
    announced.add(a.id)
    count('achievement', a.id)
  }
  try {
    for (const a of fresh) {
      await run('INSERT OR IGNORE INTO achievements (id, earned_on) VALUES (?, ?)', [a.id, a.earnedOn])
    }
    await loadAchievements()
  } catch {
    // Not ours to write; the list still shows them earned.
  }
  const names = fresh.map((a) => t(`achievement.${a.id}.title`)).join(', ')
  toast(t('achievements.unlocked', { count: fresh.length, names }), 'good')
  return fresh
}

/** Watches for new ones from boot on. */
export function startAchievements() {
  watch(achievements, () => recordAchievements(), { immediate: true })
}
